import { useEffect, useState } from 'react';
import { AlertTriangle } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { fmt } from '../shared/badges';
import { fetchReleveCca } from '../../lib/cca';

// Trois comptes STRICTEMENT distincts pour une même identité : fournisseur,
// client et compte courant d'associé. Aucun solde net global, aucune compensation.
const NATURES_FOURNISSEUR = ['achat', 'conversion'];
const NATURES_CLIENT = ['prestation', 'vente', 'emballages'];
const REF_AFFECTEE = /^(R-|V-|FL-|FG-|AV-|CONV|DEP)/i;

export const SoldesSepares = ({ compte }) => {
  const { user } = useAuth();
  const [cca, setCca] = useState(null);

  useEffect(() => {
    fetchReleveCca(compte.tiersId, user).then((r) => setCca(r?.contact_id ? r : null));
  }, [compte.tiersId, user]);

  const lignes = compte.lignes ?? [];
  const somme = (liste, champ) => liste.reduce((s, l) => s + (Number(l[champ]) || 0), 0);

  const achats = lignes.filter((l) => NATURES_FOURNISSEUR.includes(l.nature));
  const commercialClient = lignes.filter((l) => NATURES_CLIENT.includes(l.nature));
  const paiements = lignes.filter((l) => l.nature === 'paiement');
  const encaissements = lignes.filter((l) => l.nature === 'encaissement');

  // Règlements historiques non rattachables à un bon précis : signalés, jamais répartis d'office.
  const mixte = achats.length > 0 && commercialClient.length > 0;
  const aAffecter = mixte ? paiements.filter((l) => !REF_AFFECTEE.test(String(l.bon ?? ''))) : [];
  const provisoire = aAffecter.length > 0;

  const soldeFournisseur = somme(achats, 'credit') - somme(paiements, 'debit');
  const soldeClient = somme(commercialClient, 'debit') - somme(encaissements, 'credit');

  const bloc = (titre, valeur, sens, testid, accent) => (
    <div data-testid={testid} className={`border border-slate-200 border-l-4 ${accent} px-3 py-2`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
      <p className="tabular font-data text-lg font-semibold text-slate-900">
        {valeur < 0 ? '− ' : ''}
        {fmt(Math.abs(valeur), 3)} TND
      </p>
      <p className="text-[11px] text-slate-600">{sens}</p>
    </div>
  );

  return (
    <div className="mt-4 space-y-2" data-testid="soldes-separes">
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
        Comptes distincts — aucune compensation automatique entre eux
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {bloc(
          'Compte fournisseur',
          soldeFournisseur,
          soldeFournisseur > 0 ? 'L’huilerie doit ce montant (achats − paiements)' : soldeFournisseur < 0 ? 'Avances versées en excédent' : 'Compte soldé',
          'solde-fournisseur',
          'border-l-amber-500'
        )}
        {bloc(
          'Compte client',
          soldeClient,
          soldeClient > 0 ? 'Le tiers doit ce montant (ventes & prestations − encaissements)' : soldeClient < 0 ? 'Avoir en faveur du client' : 'Compte soldé',
          'solde-client',
          'border-l-sky-600'
        )}
        {cca
          ? bloc(
              'Compte courant d’associé',
              cca.solde,
              cca.solde > 0
                ? `L’entreprise doit ce montant à l’associé${cca.provisoire ? ` — ${cca.mention}` : ''}`
                : cca.solde < 0
                  ? `L’associé doit ce montant à l’entreprise${cca.provisoire ? ` — ${cca.mention}` : ''}`
                  : cca.provisoire
                    ? cca.mention
                    : 'Compte soldé',
              'solde-cca',
              'border-l-slate-900'
            )
          : (
            <div data-testid="solde-cca-absent" className="border border-dashed border-slate-300 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Compte courant d’associé</p>
              <p className="text-[11px] text-slate-500">Aucun compte CCA rattaché à cette fiche.</p>
            </div>
          )}
      </div>
      {provisoire && (
        <p data-testid="soldes-a-affecter" className="flex items-start gap-1 border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {aAffecter.length} règlement(s) historique(s) non rattaché(s) à un bon ou une facture :{' '}
          <strong>à affecter</strong> ({aAffecter.map((l) => l.bon || l.id).join(', ')}). Les soldes fournisseur et client
          ci-dessus sont <strong>provisoires</strong> tant que cette affectation n’est pas confirmée — aucune répartition
          n’a été décidée automatiquement.
        </p>
      )}
    </div>
  );
};
